import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileText, MapPin, Plus, Bug, Zap, CheckCircle2, Clock, Rocket, AlertCircle } from 'lucide-react';

const DevelopmentResources = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('changelog');

  // Changelog data - Update this after each feature/fix
  const changelogEntries = [
    {
      version: 'v1.10.0',
      date: '2025-01-15',
      status: 'Released',
      changes: [
        { type: 'feature', text: 'Screenshot monitoring system with automatic capture every 5 seconds' },
        { type: 'feature', text: 'Mobile responsive design across all pages' },
        { type: 'feature', text: 'Settings page reorganization with categorized sections' },
        { type: 'feature', text: 'Marketing website with landing page, pricing, features, and about pages' },
        { type: 'feature', text: 'Beautiful signup page with multi-step form' },
        { type: 'feature', text: 'Demo video page and popup modal system' },
        { type: 'improvement', text: 'Improved page load performance with optimized components' },
        { type: 'improvement', text: 'Enhanced navigation with better mobile experience' },
        { type: 'fix', text: 'Fixed PowerShell installer script file size display formatting' },
        { type: 'fix', text: 'Fixed asset details page content overlap with sidebar' }
      ]
    },
    {
      version: 'v1.9.0',
      date: '2024-12-20',
      status: 'Released',
      changes: [
        { type: 'feature', text: 'Enhanced hardware info collection (disk partitions, memory modules)' },
        { type: 'feature', text: 'Asset health scoring system with security and performance scores' },
        { type: 'feature', text: 'Centralized agent version management system' },
        { type: 'improvement', text: 'Optimized Applications tab with caching for better performance' },
        { type: 'fix', text: 'Fixed Terminal WebSocket connection by updating API prefix' },
        { type: 'fix', text: 'Resolved asset score "NA" display issue' }
      ]
    },
    {
      version: 'v1.8.0',
      date: '2024-11-30',
      status: 'Released',
      changes: [
        { type: 'feature', text: 'Client portal for ticket submission' },
        { type: 'feature', text: 'Advanced search and filtering across assets' },
        { type: 'improvement', text: 'Enhanced security with MFA support' },
        { type: 'improvement', text: 'Improved notification system' },
        { type: 'fix', text: 'Fixed timezone handling in reports' }
      ]
    }
  ];

  // Roadmap data - Update this as you plan and work on features
  const roadmapSections = [
    {
      status: 'In Progress',
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      icon: Rocket,
      items: [
        {
          title: 'User Management System',
          description: 'Complete user and role management with permissions',
          priority: 'High',
          assignedTo: 'Backend Team',
          eta: 'Q1 2025',
          progress: 40
        },
        {
          title: 'Agent v1.10.0 Deployment',
          description: 'Build and deploy C# agent with screenshot monitoring',
          priority: 'High',
          assignedTo: 'Dev Team',
          eta: 'Q1 2025',
          progress: 80
        }
      ]
    },
    {
      status: 'Planned - Next',
      color: 'bg-purple-100 text-purple-700 border-purple-300',
      icon: Clock,
      items: [
        {
          title: 'User Request System Frontend',
          description: 'Wire up frontend for user request workflows',
          priority: 'Medium',
          assignedTo: 'Frontend Team',
          eta: 'Q2 2025',
          progress: 0
        },
        {
          title: 'Live Performance Graphs',
          description: 'Real-time CPU, memory, and disk usage graphs',
          priority: 'Medium',
          assignedTo: 'Full Stack',
          eta: 'Q2 2025',
          progress: 0
        },
        {
          title: 'Dark Mode Support',
          description: 'Full dark theme for entire application',
          priority: 'Medium',
          assignedTo: 'Frontend Team',
          eta: 'Q2 2025',
          progress: 0
        }
      ]
    },
    {
      status: 'Future / Backlog',
      color: 'bg-slate-100 text-slate-700 border-slate-300',
      icon: MapPin,
      items: [
        {
          title: 'Registry Editor',
          description: 'Remote Windows Registry editor interface',
          priority: 'Low',
          assignedTo: 'Unassigned',
          eta: 'Q3 2025',
          progress: 0
        },
        {
          title: 'File Explorer',
          description: 'Remote file system browser and manager',
          priority: 'Low',
          assignedTo: 'Unassigned',
          eta: 'Q3 2025',
          progress: 0
        },
        {
          title: 'Guacamole/RustDesk Integration',
          description: 'Alternative remote desktop solutions',
          priority: 'Medium',
          assignedTo: 'Unassigned',
          eta: 'Q4 2025',
          progress: 0
        },
        {
          title: 'Network Monitoring',
          description: 'Monitor switches, routers, and network devices',
          priority: 'Medium',
          assignedTo: 'Unassigned',
          eta: 'Q4 2025',
          progress: 0
        },
        {
          title: 'Backup Management',
          description: 'Monitor and manage backups across all endpoints',
          priority: 'Medium',
          assignedTo: 'Unassigned',
          eta: 'Q1 2026',
          progress: 0
        },
        {
          title: 'Linux & Mac Support',
          description: 'Agent support for Linux and macOS endpoints',
          priority: 'High',
          assignedTo: 'Unassigned',
          eta: 'Q1 2026',
          progress: 0
        },
        {
          title: 'Platform Admin Portal',
          description: 'Separate super-admin portal to manage all tenants, onboarding, billing, and platform-wide settings (subdomain-based with enhanced security)',
          priority: 'Medium',
          assignedTo: 'Unassigned',
          eta: 'Q2 2026',
          progress: 0
        }
      ]
    }
  ];

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'feature':
        return Plus;
      case 'improvement':
        return Zap;
      case 'fix':
        return Bug;
      default:
        return CheckCircle2;
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'feature':
        return 'text-green-600 bg-green-100';
      case 'improvement':
        return 'text-blue-600 bg-blue-100';
      case 'fix':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const getChangeTypeLabel = (type) => {
    switch (type) {
      case 'feature':
        return 'New';
      case 'improvement':
        return 'Improved';
      case 'fix':
        return 'Fixed';
      default:
        return 'Changed';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'Low':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Development Resources</h1>
                <p className="text-sm text-slate-500">Track features, fixes, and roadmap</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('changelog')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'changelog'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Changelog</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('roadmap')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'roadmap'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>Roadmap</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Changelog Tab */}
        {activeTab === 'changelog' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Version History</h2>
                <p className="text-slate-600 mt-1">Complete list of features, improvements, and fixes</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <span className="text-sm font-semibold text-blue-900">Current: v{changelogEntries[0].version.replace('v', '')}</span>
              </div>
            </div>

            <div className="space-y-6">
              {changelogEntries.map((entry, index) => (
                <Card key={index} className="border-slate-200 shadow-sm">
                  <CardHeader className="border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <CardTitle className="text-xl">{entry.version}</CardTitle>
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          {entry.status}
                        </Badge>
                      </div>
                      <span className="text-sm text-slate-500">{entry.date}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ul className="space-y-3">
                      {entry.changes.map((change, changeIndex) => {
                        const Icon = getChangeTypeIcon(change.type);
                        return (
                          <li key={changeIndex} className="flex items-start">
                            <div className={`w-6 h-6 rounded flex items-center justify-center mr-3 flex-shrink-0 ${getChangeTypeColor(change.type)}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <span className={`text-xs font-semibold mr-2 ${getChangeTypeColor(change.type)} px-2 py-0.5 rounded`}>
                                {getChangeTypeLabel(change.type)}
                              </span>
                              <span className="text-slate-700">{change.text}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <h3 className="font-semibold text-indigo-900 mb-2 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                How to Update Changelog
              </h3>
              <ol className="text-sm text-indigo-800 space-y-1 list-decimal list-inside">
                <li>Open <code className="bg-indigo-100 px-2 py-0.5 rounded">/app/frontend/src/pages/DevelopmentResources.jsx</code></li>
                <li>Find the <code className="bg-indigo-100 px-2 py-0.5 rounded">changelogEntries</code> array</li>
                <li>Add new version entry at the top of the array</li>
                <li>Include version, date, status, and list of changes</li>
                <li>Use types: 'feature', 'improvement', or 'fix'</li>
              </ol>
            </div>
          </div>
        )}

        {/* Roadmap Tab */}
        {activeTab === 'roadmap' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Development Roadmap</h2>
              <p className="text-slate-600 mt-1">Track what's in progress, planned, and on the horizon</p>
            </div>

            {roadmapSections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <div className="flex items-center mb-4">
                  <section.icon className="w-6 h-6 mr-3 text-slate-600" />
                  <h3 className="text-xl font-bold text-slate-900">{section.status}</h3>
                </div>

                <div className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <Card key={itemIndex} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h4>
                            <p className="text-slate-600 text-sm">{item.description}</p>
                          </div>
                          <div className="flex flex-col items-end space-y-2 ml-4">
                            <Badge variant="outline" className={getPriorityColor(item.priority)}>
                              {item.priority}
                            </Badge>
                            <span className="text-xs text-slate-500">{item.eta}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                          <div>
                            <span className="text-xs text-slate-500">Assigned To</span>
                            <p className="text-sm font-medium text-slate-700">{item.assignedTo}</p>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500 mb-2 block">Progress</span>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-full transition-all"
                                  style={{ width: `${item.progress}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-slate-700">{item.progress}%</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <h3 className="font-semibold text-indigo-900 mb-2 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                How to Update Roadmap
              </h3>
              <ol className="text-sm text-indigo-800 space-y-1 list-decimal list-inside">
                <li>Open <code className="bg-indigo-100 px-2 py-0.5 rounded">/app/frontend/src/pages/DevelopmentResources.jsx</code></li>
                <li>Find the <code className="bg-indigo-100 px-2 py-0.5 rounded">roadmapSections</code> array</li>
                <li>Add/update items in: "In Progress", "Planned - Next", or "Future / Backlog"</li>
                <li>Set priority (High/Medium/Low), assignee, ETA, and progress %</li>
                <li>Move items between sections as work progresses</li>
              </ol>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DevelopmentResources;
